// Core
import { ReactNode, MouseEvent, MouseEventHandler, CSSProperties, Ref } from 'react';
// Packages
import { Typography as AntTypography } from 'antd';
import cn from 'classnames';
import { TTriggerEventProps } from '../../antdProps';
// Styles
import './paragraph.styles.scss';

interface IParagraphProps extends TTriggerEventProps {
  ref?: Ref<HTMLDivElement>;
  children?: ReactNode;
  code?: boolean;
  copyable?: boolean;
  className?: string;
  deleted?: boolean;
  disabled?: boolean;
  fontSize?: 'small' | 'middle' | 'large';
  editable?: boolean;
  ellipsis?: any;
  mark?: boolean;
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  strong?: boolean;
  style?: CSSProperties;
  italic?: boolean;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  underline?: boolean;
}

/**
 * Provides multiple types of text.
 * Basic text writing, including headings, body text, lists, and more.
 *
 * @description When need to display a title or paragraph contents in Articles/Blogs/Notes.
 * When you need copyable/editable/ellipsis texts.
 *
 * @param children - Some ReactNode
 * @param code - Code style
 * @param copyable - Whether to be copyable, customize it via setting an object
 * @param className - Component className
 * @param deleted - Deleted line style
 * @param disabled - Disabled content
 * @param fontSize - Element font size
 * @param editable - 	If editable. Can control edit state when is object
 * @param ellipsis - Display ellipsis when text overflows, can configure rows and expandable by using object
 * @param mark - Marked style
 * @param onClick - Set the handler to handle click event
 * @param strong - Bold style
 * @param style - To customize the styles
 * @param italic - Italic style
 * @param type - Content type
 * @param underline - Underlined style
 */

const Paragraph = ({
  children,
  code = false,
  copyable = false,
  className,
  deleted = false,
  disabled = false,
  fontSize = 'middle',
  editable = false,
  ellipsis = false,
  mark = false,
  onClick,
  strong = false,
  style,
  italic = false,
  type,
  underline = false,
  ref,
  ...triggerProps
}: IParagraphProps) => {
  const classNames = cn(className, [fontSize], { strong });

  return (
    <AntTypography.Paragraph
      ref={ref}
      {...triggerProps}
      code={code}
      copyable={copyable}
      className={classNames}
      delete={deleted}
      disabled={disabled}
      editable={editable}
      ellipsis={ellipsis}
      mark={mark}
      onClick={onClick as MouseEventHandler<HTMLElement>}
      style={style}
      italic={italic}
      type={type}
      underline={underline}
    >
      {children}
    </AntTypography.Paragraph>
  );
};

export default Paragraph;
